import React, { createContext, useReducer, ReactNode, Dispatch, useEffect } from 'react';
import jwt_decode from 'jwt-decode';
import { getLocation } from '../utils/getLocation';

// Define the shape of your global state
import { Domain } from '../types/domain';

interface GlobalState {
  allServices: any[];
  userId: string | null;
  domain: string | null;
  current: number;
  selectedService: any;
  cart: any[];
  suggestion: any[];
  activeMenu: string;
  activeDashboardTab: string; // Added activeDashboardTab
  details: any;
  usersData: any[];
  currentUser: any;
  managerList: any[];
  user: any;
  users: any[];
  managers: any[];
  subscriptions: any[];
  orders: any[];
  templateOrders: any[];
  templateOrder: any;
  invoices: any[];
  insights: any[];
  wpuser: any;
  emailuser: any;
  domains: Domain[];
  domainsData: Domain[];
  web: any;
  articles: any[];
  duration: any;
  discount: any;
  hosting: any;
  orderId: any;
  emails: any[];
  email: any;
  currentSelectedDomain: string | null; // New state variable
  domainSearchResults: any[];
  usersLoading: boolean;
  location: {
    countryCode: string;
    countryName: string;
    continentCode: string;
    currency: string;
    ip: string;
  } | null;
}

// Define action types
type Action =
  | { type: 'SET_ALL_SERVICES'; payload: any[] }
  | { type: 'SET_USER_ID'; payload: string | null }
  | { type: 'SET_DOMAIN'; payload: string | null }
  | { type: 'SET_CURRENT'; payload: number }
  | { type: 'SET_SELECTED_SERVICE'; payload: any }
  | { type: 'SET_CART'; payload: any[] }
  | { type: 'SET_SUGGESTION'; payload: any[] }
  | { type: 'SET_ACTIVE_MENU'; payload: string }
  | { type: 'SET_ACTIVE_DASHBOARD_TAB'; payload: string }
  | { type: 'SET_DETAILS'; payload: any }
  | { type: 'SET_USERS_DATA'; payload: any[] }
  | { type: 'SET_USERS_LOADING'; payload: boolean }
  | { type: 'SET_CURRENT_USER'; payload: any }
  | { type: 'SET_MANAGER_LIST'; payload: any[] }
  | { type: 'SET_USER'; payload: any }
  | { type: 'SET_USERS'; payload: any[] }
  | { type: 'SET_MANAGERS'; payload: any[] }
  | { type: 'SET_SUBSCRIPTIONS'; payload: any[] }
  | { type: 'SET_ORDERS'; payload: any[] }
  | { type: 'SET_TEMPLATE_ORDERS'; payload: any[] }
  | { type: 'SET_TEMPLATE_ORDER'; payload: any }
  | { type: 'SET_INVOICES'; payload: any[] }
  | { type: 'SET_INSIGHTS'; payload: any[] }
  | { type: 'SET_WP_USER'; payload: any }
  | { type: 'SET_EMAIL_USER'; payload: any }
  | { type: 'SET_DOMAINS'; payload: any[] }
  | { type: 'SET_DOMAINS_DATA'; payload: any[] }
  | { type: 'SET_WEB'; payload: any }
  | { type: 'SET_ARTICLES'; payload: any[] }
  | { type: 'SET_DURATION'; payload: any }
  | { type: 'SET_DISCOUNT'; payload: any }
  | { type: 'SET_HOSTING'; payload: any }
  | { type: 'SET_ORDER_ID'; payload: any }
  | { type: 'SET_EMAILS'; payload: any[] }
  | { type: 'SET_EMAIL'; payload: any }
  | { type: 'SET_CURRENT_SELECTED_DOMAIN'; payload: string | null } // New action type
  | { type: 'SET_DOMAIN_SEARCH_RESULTS'; payload: any[] }
  | { type: 'UPDATE_DOMAIN_SEARCH_RESULT'; payload: any }
  | { type: 'SET_LOCATION'; payload: any };


// Initial state
const getInitialUser = () => {
  const token = localStorage.getItem('token');
  if (token) {
    try {
      const decoded: any = jwt_decode(token);
      const currentTime = Date.now() / 1000;
      if (decoded.exp && decoded.exp < currentTime) {
        localStorage.removeItem('token');
        return null;
      }
      return decoded;
    } catch (error) {
      console.error('Error decoding token for initial state:', error);
      return null;
    }
  }
  return null;
};

const getInitialCart = () => {
  const cart = localStorage.getItem('cart');
  if (cart) {
    try {
      return JSON.parse(cart);
    } catch (error) {
      console.error('Error parsing cart from local storage:', error);
      return [];
    }
  }
  return [];
};

const getInitialCurrent = () => {
  const currentStep = localStorage.getItem('currentStep');
  if (currentStep) {
    return parseInt(currentStep, 10) || 0;
  }
  return 0;
};

const getInitialUserId = () => {
  return localStorage.getItem('checkoutUserId') || null;
};

const getInitialDomain = () => {
  return localStorage.getItem('checkoutDomain') || null;
};

const getInitialOrderId = () => {
  return localStorage.getItem('checkoutOrderId') || null;
};

const initialState: GlobalState = {
  allServices: [],
  userId: getInitialUserId(),
  domain: getInitialDomain(),
  current: getInitialCurrent(),
  selectedService: null,
  cart: getInitialCart(),
  suggestion: [],
  activeMenu: 'dashboard', // Default value from Dashboard.tsx
  activeDashboardTab: 'recentActivity', // Initialize new state variable
  details: null,
  usersData: [],
  usersLoading: false,
  currentUser: null,
  managerList: [],
  user: getInitialUser(),
  users: [],
  managers: [],
  subscriptions: [],
  orders: [],
  templateOrders: [],
  templateOrder: null,
  invoices: [],
  insights: [],
  wpuser: null,
  emailuser: null,
  domains: [],
  web: null,
  articles: [],
  duration: 1,
  discount: null,
  hosting: null,
  orderId: getInitialOrderId(),
  emails: [],
  email: null,
  domainsData: [],
  currentSelectedDomain: null, // Initialize new state variable
  domainSearchResults: [],
  location: null,
};

// Reducer function
const globalReducer = (state: GlobalState, action: Action): GlobalState => {
  switch (action.type) {
    case 'SET_ALL_SERVICES':
      return { ...state, allServices: action.payload };
    case 'SET_USER_ID':
      return { ...state, userId: action.payload };
    case 'SET_DOMAIN':
      return { ...state, domain: action.payload };
    case 'SET_CURRENT':
      return { ...state, current: action.payload };
    case 'SET_SELECTED_SERVICE':
      return { ...state, selectedService: action.payload };
    case 'SET_CART':
      return { ...state, cart: action.payload };
    case 'SET_SUGGESTION':
      return { ...state, suggestion: action.payload };
    case 'SET_ACTIVE_MENU':
      return { ...state, activeMenu: action.payload };
    case 'SET_ACTIVE_DASHBOARD_TAB':
      return { ...state, activeDashboardTab: action.payload };
    case 'SET_DETAILS':
      return { ...state, details: action.payload };
    case 'SET_USERS_DATA':
      return { ...state, usersData: action.payload, usersLoading: false };
    case 'SET_USERS_LOADING':
      return { ...state, usersLoading: action.payload };
    case 'SET_CURRENT_USER':
      return { ...state, currentUser: action.payload };
    case 'SET_MANAGER_LIST':
      return { ...state, managerList: action.payload };
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'SET_USERS':
      return { ...state, users: action.payload };
    case 'SET_MANAGERS':
      return { ...state, managers: action.payload };
    case 'SET_SUBSCRIPTIONS':
      return { ...state, subscriptions: action.payload };
    case 'SET_ORDERS':
      return { ...state, orders: action.payload };
    case 'SET_TEMPLATE_ORDERS':
      return { ...state, templateOrders: action.payload };
    case 'SET_TEMPLATE_ORDER':
      return { ...state, templateOrder: action.payload };
    case 'SET_INVOICES':
      return { ...state, invoices: action.payload };
    case 'SET_INSIGHTS':
      return { ...state, insights: action.payload };
    case 'SET_WP_USER':
      return { ...state, wpuser: action.payload };
    case 'SET_EMAIL_USER':
      return { ...state, emailuser: action.payload };
    case 'SET_DOMAINS':
      return { ...state, domains: action.payload };
    case 'SET_DOMAINS_DATA':
      return { ...state, domainsData: action.payload };
    case 'SET_WEB':
      return { ...state, web: action.payload };
    case 'SET_ARTICLES':
      return { ...state, articles: action.payload };
    case 'SET_DURATION':
      return { ...state, duration: action.payload };
    case 'SET_DISCOUNT':
      return { ...state, discount: action.payload };
    case 'SET_HOSTING':
      return { ...state, hosting: action.payload };
    case 'SET_ORDER_ID':
      return { ...state, orderId: action.payload };
    case 'SET_EMAILS':
      return { ...state, emails: action.payload };
    case 'SET_EMAIL':
      return { ...state, email: action.payload };
    case 'SET_CURRENT_SELECTED_DOMAIN': // New reducer case
      return { ...state, currentSelectedDomain: action.payload };
    case 'SET_DOMAIN_SEARCH_RESULTS':
      return { ...state, domainSearchResults: action.payload };
    case 'UPDATE_DOMAIN_SEARCH_RESULT':
      return {
        ...state,
        domainSearchResults: state.domainSearchResults.map(r => r.domain === action.payload.domain ? action.payload : r)
      };
    case 'SET_LOCATION':
      return { ...state, location: action.payload };
    default:
      return state;
  }
};

// Create the context
export const GlobalContext = createContext<{
  state: GlobalState;
  dispatch: Dispatch<Action>;
}>({
  state: initialState,
  dispatch: () => null, // Dummy dispatch function
});

// Create the provider component
interface GlobalProviderProps {
  children: ReactNode;
}

export const GlobalProvider: React.FC<GlobalProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(globalReducer, initialState);
  const currentStep = state.current;

  // A successful refresh replaces the access JWT without replacing the page.
  // Keep the dashboard role and user identity synchronized with the new JWT.
  useEffect(() => {
    const syncRefreshedToken = () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const claims: any = jwt_decode(token);
        dispatch({ type: 'SET_USER', payload: claims });
      } catch {
        // The route guard will handle an unusable token.
      }
    };
    const onOtherTabSessionChange = (event: StorageEvent) => {
      if (event.key !== 'token') return;
      if (event.newValue) syncRefreshedToken();
      else dispatch({ type: 'SET_USER', payload: null });
    };
    window.addEventListener('kitech:auth-refreshed', syncRefreshedToken);
    window.addEventListener('storage', onOtherTabSessionChange);
    return () => {
      window.removeEventListener('kitech:auth-refreshed', syncRefreshedToken);
      window.removeEventListener('storage', onOtherTabSessionChange);
    };
  }, []);

  useEffect(() => {
    const fetchLocation = async () => {
      const loc = await getLocation();
      dispatch({ type: 'SET_LOCATION', payload: loc });
    };
    fetchLocation();
  }, []);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(state.cart));
    if (state.cart.length === 0) {
      localStorage.setItem('currentStep', '0');
      localStorage.removeItem('checkoutUserId');
      localStorage.removeItem('checkoutDomain');
      localStorage.removeItem('checkoutOrderId');
      localStorage.removeItem('checkoutZohoRequest');
    }
  }, [state.cart]);

  useEffect(() => {
    localStorage.setItem('currentStep', currentStep.toString());
  }, [currentStep]);

  useEffect(() => {
    if (state.userId) {
      localStorage.setItem('checkoutUserId', state.userId);
    } else {
      localStorage.removeItem('checkoutUserId');
    }
  }, [state.userId]);

  useEffect(() => {
    if (state.domain) {
      localStorage.setItem('checkoutDomain', state.domain);
    } else {
      localStorage.removeItem('checkoutDomain');
    }
  }, [state.domain]);

  useEffect(() => {
    if (state.orderId) {
      localStorage.setItem('checkoutOrderId', state.orderId);
    } else {
      localStorage.removeItem('checkoutOrderId');
    }
  }, [state.orderId]);

  useEffect(() => {
    const syncUser = () => {
      if (!state.user) {
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const decoded: any = jwt_decode(token);
            const currentTime = Date.now() / 1000;
            if (decoded.exp && decoded.exp > currentTime) {
              dispatch({ type: 'SET_USER', payload: decoded });
            }
          } catch (error) {
            console.error('Error syncing user from token:', error);
          }
        }
      }
    };
    syncUser();
  }, [state.user]);

  useEffect(() => {
    const storedDomainSearchResults = sessionStorage.getItem('domainSearchResults');
    if (storedDomainSearchResults) {
      dispatch({ type: 'SET_DOMAIN_SEARCH_RESULTS', payload: JSON.parse(storedDomainSearchResults) });
    }
  }, []); // Empty dependency array means this effect runs once on mount

  useEffect(() => {
    sessionStorage.setItem('domainSearchResults', JSON.stringify(state.domainSearchResults));
  }, [state.domainSearchResults]);

  const contextValue = React.useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return (
    <GlobalContext.Provider value={contextValue}>
      {children}
    </GlobalContext.Provider>
  );
};
